import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, SkipForward, SkipBack, Maximize, Minimize, Volume2 } from 'lucide-react';
import { usePlayerStore, usePlaybackState, usePlayerControls } from './playerStore';

interface PlayerControlsProps {
  className?: string;
}

// Global flag to prevent multiple keyboard listeners
let keyboardListenerActive = false;

export const PlayerControls: React.FC<PlayerControlsProps> = ({ className = '' }) => {
  const { isPlaying, currentTime, duration, playbackSpeed } = usePlaybackState();
  const { togglePlayPause, seek, setPlaybackSpeed } = usePlayerControls();
  const { isFullscreen, toggleFullscreen } = usePlayerStore();
  
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [tempProgress, setTempProgress] = useState<number | null>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  
  // Format time in mm:ss
  const formatTime = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // Calculate progress percentage (use temp progress during dragging for instant feedback)
  const progress = tempProgress !== null ? tempProgress : (duration > 0 ? (currentTime / duration) * 100 : 0);
  
  // Handle progress bar click/drag
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(x / rect.width, 1));
    const newTime = percentage * duration;
    
    // Set temp progress for instant visual feedback
    setTempProgress(percentage * 100);
    seek(newTime);
  };
  
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    handleProgressClick(e);
  };
  
  // Handle single click on progress bar
  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only handle if not dragging (to avoid double handling)
    if (!isDragging) {
      handleProgressClick(e);
      // Clear temp progress after delay for single clicks too
      setTimeout(() => {
        setTempProgress(null);
      }, 100);
    }
  };
  
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !progressBarRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = Math.max(0, Math.min(x / rect.width, 1));
    const newTime = percentage * duration;
    
    // Set temp progress for instant visual feedback during drag
    setTempProgress(percentage * 100);
    seek(newTime);
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
    // Clear temp progress after a short delay to let the actual currentTime catch up
    setTimeout(() => {
      setTempProgress(null);
    }, 100);
  };
  
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);
  
  // Keyboard shortcuts
  useEffect(() => {
    // Only add keyboard listener once
    if (keyboardListenerActive) {
      return;
    }
    keyboardListenerActive = true;
    
    const handleKeyPress = (e: KeyboardEvent) => {
      // Prevent handling if target is an input element
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      switch (e.key) {
        case ' ':
          e.preventDefault();
          e.stopPropagation();
          togglePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seek(Math.max(0, currentTime - 5000));
          break;
        case 'ArrowRight':
          e.preventDefault();
          seek(Math.min(duration, currentTime + 5000));
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      keyboardListenerActive = false;
    };
  }, [currentTime, duration, togglePlayPause, seek, toggleFullscreen]);
  
  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];
  
  return (
    <div className={`bg-gray-900/95 backdrop-blur text-white px-4 pt-2 pb-4 ${className}`} style={{ paddingBottom: `calc(1rem + env(safe-area-inset-bottom))` }}>
      {/* Progress Bar */}
      <div className="mb-2">
        <div 
          ref={progressBarRef}
          className="relative h-1 bg-gray-700/50 rounded-full cursor-pointer group"
          onMouseDown={handleMouseDown}
          onClick={handleProgressBarClick}
        >
          <div 
            className={`absolute h-full bg-red-600 rounded-full ${isDragging ? '' : 'transition-all duration-150'}`}
            style={{ width: `${progress}%` }}
          />
          <div 
            className={`absolute w-3 h-3 bg-white rounded-full -mt-1 -ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity ${isDragging ? '' : 'transition-all duration-150'}`}
            style={{ left: `${progress}%` }}
          />
        </div>
      </div>
      
      {/* Control Buttons - Minimalist */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {/* Play/Pause */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              togglePlayPause();
            }}
            className="p-2 hover:bg-gray-800/50 rounded-full transition-colors"
            title={isPlaying ? 'Duraklat (Space)' : 'Oynat (Space)'}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
          </button>
          
          {/* Skip Forward */}
          <button
            onClick={() => seek(Math.min(duration, currentTime + 10000))}
            className="p-2 hover:bg-gray-800/50 rounded-full transition-colors"
            title="10 saniye ileri (→)"
          >
            <SkipForward size={18} />
          </button>
          
          {/* Time */}
          <div className="text-xs text-gray-400 ml-2">
            <span className="font-mono">{formatTime(currentTime)}</span>
            <span className="mx-1">/</span>
            <span className="font-mono">{formatTime(duration)}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Speed Control */}
          <div className="relative">
            <button
              onClick={() => setShowSpeedMenu(!showSpeedMenu)}
              className="px-2 py-1 hover:bg-gray-800/50 rounded text-xs"
            >
              {playbackSpeed}x
            </button>
            
            {showSpeedMenu && (
              <div className="absolute bottom-full mb-1 right-0 bg-gray-800 rounded shadow-lg py-1 min-w-[60px]">
                {speedOptions.map((speed) => (
                  <button
                    key={speed}
                    onClick={() => {
                      setPlaybackSpeed(speed);
                      setShowSpeedMenu(false);
                    }}
                    className={`block w-full px-2 py-1 text-xs hover:bg-gray-700 transition-colors text-left ${
                      playbackSpeed === speed ? 'text-red-600' : ''
                    }`}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Fullscreen */}
          <button
            onClick={toggleFullscreen}
            className="p-2 hover:bg-gray-800/50 rounded-full transition-colors"
            title={isFullscreen ? 'Tam ekrandan çık (F)' : 'Tam ekran (F)'}
          >
            {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
};